import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ErrorBanner from './components/Errors/ErrorBanner/ErrorBanner';

/**
 * The Error Boundary Class is used in order to intercept Component Error
 * and to display a message
 */
class ErrorBoundary extends Component {
  state = {
    debugMode: window.reactAppEnv.features.reportError === 'true',
    showBanner: false,
    errorType: 'api',
  };

  componentDidUpdate(prevProps) {
    const { errorApi, errorBoundary } = this.props;

    if (errorApi !== prevProps.errorApi) {
      if (errorApi.display) {
        this.setState({
          showBanner: true,
          errorType: 'api',
        });
      }
    }
    if (errorBoundary !== prevProps.errorBoundary) {
      if (errorBoundary.display) {
        this.setState({
          showBanner: true,
          errorType: 'client',
        });
      }
    }
  }

  handleClose = () => {
    this.setState({ showBanner: false });
  };

  /**
   * Send Error to the ApiService
   * @param   {integer} columnNumber The number of column error
   * @param   {string}  stack        The stack of the error
   * @param   {integer} lineNumber   The line number of the error
   * @param   {string}  message      The message of the error
   * @param   {string}  fileName     The file name of the application
   */
  sendError = (columnNumber, stack, lineNumber, message, fileName) => {
    const { debugMode } = this.state;
    const { sendingError } = this.props;

    if (debugMode) {
      sendingError({
        columnNumber, stack, lineNumber, message, fileName,
      });
    }
  };

  /**
   * ComponentDidCatch in order to catch error event
   * @param   {Object} error     The error thrown by the encapsuled components
   * @param   {Object} errorInfo The error info thrown by the encapsuled components
   */
  componentDidCatch(error, errorInfo) {
    this.sendError(error.columnNumber, errorInfo.componentStack, error.lineNumber, error.toString(), error.fileName);
  }

  /**
   * Render the error boundary component
   * @returns {Object} The error boundary component rendered
   */
  render() {
    const {
      children, errorApi, errorBoundary, pathname,
    } = this.props;
    const { debugMode, showBanner, errorType } = this.state;

    return (
      <div>
        {children}
        {(debugMode && showBanner) && (
          <div style={{ marginBottom: 30 }}>
            <ErrorBanner
              error={errorType === 'api' ? errorApi : errorBoundary}
              errorType={errorType}
              pathname={pathname}
              onClose={this.handleClose}
            />
          </div>
        )}
      </div>
    );
  }
}

ErrorBoundary.defaultProps = {
  errorApi: {},
  errorBoundary: {},
};

/**
 * Validation of the props
 */
ErrorBoundary.propTypes = {
  /** Object api error properties */
  errorApi: PropTypes.shape(),
  /** Object client error properties */
  errorBoundary: PropTypes.shape(),
  /** String actual route pathname */
  pathname: PropTypes.string.isRequired,
  /** Node app children */
  children: PropTypes.node.isRequired,
  /** Function to report error */
  sendingError: PropTypes.func.isRequired,
};

export default ErrorBoundary;
